'use client';

import { apiClient } from '@/lib/api-client';
import { useFilterStore } from '@/stores/filter-store';
import { useEffect, useState } from 'react';
import { MultiSelectSection } from './multi-select-section';
import { SelectSection } from './select-section';

interface Option {
  value: string;
  label: string;
}

interface StoreDependentSelectProps {
  type: 'dealer' | 'cardDealer' | 'cat';
  values: string[] | string;
  onValuesChange: (values: string[] | string) => void;
  placeholder?: string;
  multiSelect?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function StoreDependentSelect({
  type,
  values,
  onValuesChange,
  placeholder = '선택하세요',
  multiSelect = true,
  size = 'sm',
}: StoreDependentSelectProps) {
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);

  const { store } = useFilterStore();

  // 주유소 선택 상태 모니터링
  useEffect(() => {
    const hasSingleStore = store.length === 1;

    if (hasSingleStore) {
      // 단일 주유소 선택시 해당 주유소의 옵션들을 로드
      loadStoreSpecificOptions(store[0]);
    } else {
      // 주유소가 선택되지 않았거나 여러 개 선택시 옵션 초기화
      setOptions([]);
      if (multiSelect) {
        // 이미 비어있지 않은 경우에만 초기화 호출 (불필요한 렌더링 방지)
        if (Array.isArray(values) && values.length > 0) {
          onValuesChange([]);
        }
      } else {
        if (typeof values === 'string' && values) {
          onValuesChange('');
        }
      }
    }
    // onValuesChange는 의존성에서 제외하여 렌더마다 effect 재실행을 방지
    // 값-초기화 여부 판단을 위해 values는 포함
  }, [store, multiSelect, values]);

  const loadStoreSpecificOptions = async (storeCode: string) => {
    setLoading(true);
    try {
      let apiUrl = '';
      let dataProcessor: (data: any) => Option[] = () => [];

      switch (type) {
        case 'dealer':
          apiUrl = `master/dealer/stores/${storeCode}/codes`;
          // 이름이 같은 거래처가 있어 코드까지 보여야 구분된다(검색도 코드로 걸린다)
          dataProcessor = data =>
            (data.result || []).map((item: any) => ({
              value: item.dealCode,
              label: `${item.dealCode} ${item.dealName}`,
            }));
          break;

        case 'cardDealer':
          apiUrl = `master/dealer/stores/${storeCode}/card-dealer-codes`;
          dataProcessor = data =>
            (data.result || []).map((item: any) => ({
              value: item.dealCode,
              label: item.dealName,
            }));
          break;

        case 'cat':
          apiUrl = `sales/cardsales/stores/${storeCode}/cat`;
          dataProcessor = data =>
            (data.result || []).map((catId: string) => ({
              value: catId,
              label: catId,
            }));
          break;
      }

      const response = await apiClient.get(apiUrl);
      const data = await response.json();
      const processedOptions = dataProcessor(data);

      setOptions(processedOptions);

      // 기존 선택값이 새로운 옵션에 없으면 초기화
      if (multiSelect) {
        const validValues = (values as string[]).filter(v =>
          processedOptions.some(opt => opt.value === v)
        );
        if (validValues.length !== (values as string[]).length) {
          onValuesChange(validValues);
        }
      } else {
        const hasValidValue = processedOptions.some(
          opt => opt.value === values
        );
        if (!hasValidValue && values) {
          onValuesChange('');
        }
      }
    } catch (error) {
      console.error(
        `Failed to load ${type} options for store ${storeCode}:`,
        error
      );
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = loading || store.length !== 1;

  return (
    <div>
      {multiSelect ? (
        <MultiSelectSection
          values={values as string[]}
          onValuesChange={onValuesChange as (values: string[]) => void}
          options={options}
          placeholder={
            loading
              ? '로딩 중...'
              : store.length === 0
                ? '주유소를 먼저 선택하세요'
                : store.length > 1
                  ? '주유소를 1개만 선택하세요'
                  : placeholder
          }
          disabled={isDisabled}
          searchable={options.length > 10}
          size={size}
        />
      ) : (
        <SelectSection
          value={values as string}
          onValueChange={onValuesChange as (value: string) => void}
          options={options}
          placeholder={
            loading
              ? '로딩 중...'
              : store.length === 0
                ? '주유소를 먼저 선택하세요'
                : store.length > 1
                  ? '주유소를 1개만 선택하세요'
                  : placeholder
          }
          disabled={isDisabled}
          size={size}
        />
      )}
    </div>
  );
}
